import {
  AcademicCapIcon,
  BeakerIcon,
  BookOpenIcon,
  BriefcaseIcon,
  PlayCircleIcon,
  VideoCameraIcon,
} from '@heroicons/react/24/outline';
import type { Route } from 'next';
import Link from 'next/link';
import type { ComponentType, ReactNode, SVGProps } from 'react';

import { umamiTrack } from '../../../utils/umami';
import { Button } from '../../tailwind-ui';

import { DesktopQuickTour } from './actions/DesktopQuickTour';
import { MobileQuickTour } from './actions/MobileQuickTour';

interface NavbarItemsBase {
  name: string;
  /**
   * The type of the navbar item.
   * Basic is used for items that are shown in the main navbar or in the dropdown.
   * Footer is used for items that are only shown in the footer of the dropdown.
   * Button is used for items that are shown as buttons at the right position.
   */
  type: 'basic' | 'button';
  className?: string;
  icon?: ComponentType<SVGProps<SVGSVGElement>>;
  hideOnMobile?: boolean;
  hideOnNmriumPage?: boolean;
}

export interface NavbarItemsGroup extends NavbarItemsBase {
  group: NavbarItem[];
}

export interface NavbarItemsLink extends NavbarItemsBase {
  href: Route;
  blank?: boolean;
}

export interface NavbarItemAction extends NavbarItemsBase {
  largeAction: (
    name: string,
    icon?: ComponentType<SVGProps<SVGSVGElement>>,
  ) => ReactNode;
  smallAction: (
    name: string,
    setMobileMenuOpen: (isOpen: boolean) => void,
    icon?: ComponentType<SVGProps<SVGSVGElement>>,
  ) => ReactNode;
}

export type NavbarItem = NavbarItemsLink | NavbarItemAction | NavbarItemsGroup;
export type NavbarItems = NavbarItem[];

export const items: NavbarItems = [
  { name: 'Features', href: '/features', type: 'basic' },
  { name: 'Pricing', href: '/pricing', type: 'basic' },
  {
    name: 'Teaching',
    group: [
      {
        name: 'NMRium for teachers',
        href: '/teaching',
        type: 'basic',
        icon: AcademicCapIcon,
      },
      {
        name: 'Predict',
        href: '/predict' as Route,
        type: 'basic',
        icon: BriefcaseIcon,
        className: 'hidden lg:inline-flex',
      },
      {
        name: 'Simulate',
        href: '/simulation',
        type: 'basic',
        icon: BriefcaseIcon,
        className: 'hidden lg:inline-flex',
      },
    ],
    type: 'basic',
  },
  {
    name: 'Resources',
    type: 'basic',
    group: [
      {
        name: 'Features',
        href: '/features',
        type: 'basic',
        icon: BeakerIcon,
        hideOnMobile: true,
      },
      {
        name: 'Quick tour',
        largeAction: (name, icon) => (
          <DesktopQuickTour name={name} icon={icon} />
        ),
        smallAction: (name, setMobileMenuOpen, icon) => (
          <MobileQuickTour
            name={name}
            setMobileMenuOpen={setMobileMenuOpen}
            icon={icon}
          />
        ),
        type: 'button',
        icon: PlayCircleIcon,
      },
      {
        name: 'Tutorials',
        href: '/tutorials',
        type: 'basic',
        icon: VideoCameraIcon,
      },
      {
        name: 'Documentation',
        href: 'https://docs.nmrium.org/',
        blank: true,
        type: 'basic',
        icon: BookOpenIcon,
      },
    ],
  },
  {
    name: 'Try it',
    type: 'button',
    hideOnNmriumPage: true,
    hideOnMobile: true,
    largeAction: NMRiumButton,
    smallAction: NMRiumButton,
  },
];

// eslint-disable-next-line react-refresh/only-export-components
function NMRiumButton() {
  return (
    <Button as={Link} href="/nmrium/demo" onClick={() => umamiTrack('demo')}>
      Try it out
    </Button>
  );
}
